import {
  AdminUser,
  AdminUserDetail,
  CreateUserRequest,
  UpdateUserRequest,
  UserFilters,
  PaginatedUsersResponse,
  UserStatistics,
  AvailableRole,
  AdminResetPasswordRequest,
  UserSession,
  PaginatedLoginHistory,
  UserAddress,
  CreateUserAddressRequest,
  UpdateUserAddressRequest,
  BulkUserActionRequest,
  BulkRoleAssignmentRequest,
  BulkUserActionResponse,
} from "../types/admin";
import { ApiResponse } from "../types/common";

import apiClient, { ApiClient } from "./apiClient";

const adminUserService = new ApiClient<AdminUserDetail>("/admin/users");

// Helper function to normalize roles from API response
const normalizeUserRoles = (user: any) => {
  if (user.roles && Array.isArray(user.roles)) {
    user.roles = user.roles.map((role: any) =>
      typeof role === "string" ? role : role.name || role.id || role,
    );
  }
  return user;
};

export const getUsers = (
  filters?: UserFilters,
): Promise<ApiResponse<PaginatedUsersResponse>> => {
  return adminUserService
    .custom("get", "", undefined, { params: filters })
    .then((response: ApiResponse<any>) => {
      // Normalize roles in user list
      if (response.data?.users && Array.isArray(response.data.users)) {
        response.data.users = response.data.users.map(normalizeUserRoles);
      }
      return response;
    });
};

export const getUserDetail = (
  userId: string,
): Promise<ApiResponse<AdminUserDetail>> => {
  return adminUserService.get(userId).then((response: ApiResponse<any>) => {
    // Normalize roles in user detail
    if (response.data) {
      response.data = normalizeUserRoles(response.data);
    }
    return response;
  });
};

export const createUser = (
  userData: CreateUserRequest,
): Promise<ApiResponse<AdminUserDetail>> => {
  return adminUserService.create(userData);
};

export const updateUser = (
  userId: string,
  userData: UpdateUserRequest,
): Promise<ApiResponse<AdminUserDetail>> => {
  return adminUserService.update(userId, userData);
};

export const deleteUser = (userId: string): Promise<ApiResponse<object>> => {
  return adminUserService.custom("delete", `/${userId}`);
};

export const activateUser = (userId: string): Promise<ApiResponse<object>> => {
  return adminUserService.custom("post", `/${userId}/activate`);
};

export const deactivateUser = (
  userId: string,
): Promise<ApiResponse<object>> => {
  return adminUserService.custom("post", `/${userId}/deactivate`);
};

export const verifyUserEmail = (
  userId: string,
): Promise<ApiResponse<object>> => {
  return adminUserService.custom("post", `/${userId}/verify-email`);
};

export const assignRole = (
  userId: string,
  roleId: string,
): Promise<ApiResponse<object>> => {
  return adminUserService.custom("post", `/${userId}/roles/${roleId}`);
};

export const removeRole = (
  userId: string,
  roleId: string,
): Promise<ApiResponse<object>> => {
  return adminUserService.custom("delete", `/${userId}/roles/${roleId}`);
};

export const getAvailableRoles = (): Promise<ApiResponse<AvailableRole[]>> => {
  return adminUserService.custom("get", "/roles");
};

export const getUserStatistics = (): Promise<ApiResponse<UserStatistics>> => {
  return adminUserService.custom("get", "/statistics");
};

export const exportUsers = (filters: UserFilters): Promise<Blob> => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "")
      params.append(key, value.toString());
  });

  return apiClient
    .get(`/admin/users/export?${params.toString()}`, { responseType: "blob" })
    .then((response) => {
      // Error codes embedded in blob (e.g. USER_EXPORT_LIMIT_EXCEEDED)
      if ((response.headers["content-type"] as string)?.includes("application/json")) {
        return response.data.text().then((text: string) => {
          throw new Error(JSON.parse(text)?.message || "USER_EXPORT_FAILED");
        });
      }
      return response.data as Blob;
    });
};

export const downloadFile = (
  blob: Blob,
  filename = "users_export.csv",
): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Feature 1: Unlock user
export const unlockUser = (userId: string): Promise<ApiResponse<object>> => {
  return adminUserService.custom("post", `/${userId}/unlock`);
};

// Feature 2: Admin password reset
export const adminResetPassword = (
  userId: string,
  data: AdminResetPasswordRequest,
): Promise<ApiResponse<object>> => {
  return adminUserService.custom("post", `/${userId}/reset-password`, data);
};

// Feature 3: Session management
export const getUserSessions = (
  userId: string,
): Promise<ApiResponse<UserSession[]>> => {
  return adminUserService.custom("get", `/${userId}/sessions`);
};

export const revokeAllSessions = (
  userId: string,
): Promise<ApiResponse<object>> => {
  return adminUserService.custom("post", `/${userId}/revoke-sessions`);
};

export const revokeSession = (
  userId: string,
  sessionId: string,
): Promise<ApiResponse<object>> => {
  return adminUserService.custom("delete", `/${userId}/sessions/${sessionId}`);
};

// Feature 4: Address management
export const getUserAddresses = (
  userId: string,
): Promise<ApiResponse<UserAddress[]>> => {
  return adminUserService.custom("get", `/${userId}/addresses`);
};

export const createUserAddress = (
  userId: string,
  data: CreateUserAddressRequest,
): Promise<ApiResponse<UserAddress>> => {
  return adminUserService.custom("post", `/${userId}/addresses`, data);
};

export const updateUserAddress = (
  userId: string,
  addressId: string,
  data: UpdateUserAddressRequest,
): Promise<ApiResponse<UserAddress>> => {
  return adminUserService.custom("put", `/${userId}/addresses/${addressId}`, data);
};

export const deleteUserAddress = (
  userId: string,
  addressId: string,
): Promise<ApiResponse<object>> => {
  return adminUserService.custom("delete", `/${userId}/addresses/${addressId}`);
};

export const setDefaultAddress = (
  userId: string,
  addressId: string,
): Promise<ApiResponse<object>> => {
  return adminUserService.custom("post", `/${userId}/addresses/${addressId}/set-default`);
};

// Feature 5: Login history
export const getUserLoginHistory = (
  userId: string,
  page = 1,
  pageSize = 20,
): Promise<ApiResponse<PaginatedLoginHistory>> => {
  return adminUserService.custom("get", `/${userId}/login-history`, undefined, {
    params: { page, pageSize },
  });
};

// Feature 6: Bulk operations
export const bulkActivateUsers = (
  data: BulkUserActionRequest,
): Promise<ApiResponse<BulkUserActionResponse>> => {
  return adminUserService.custom("post", "/bulk/activate", data);
};

export const bulkDeactivateUsers = (
  data: BulkUserActionRequest,
): Promise<ApiResponse<BulkUserActionResponse>> => {
  return adminUserService.custom("post", "/bulk/deactivate", data);
};

export const bulkAssignRole = (
  data: BulkRoleAssignmentRequest,
): Promise<ApiResponse<BulkUserActionResponse>> => {
  return adminUserService.custom("post", "/bulk/assign-role", data);
};

export const bulkRemoveRole = (
  data: BulkRoleAssignmentRequest,
): Promise<ApiResponse<BulkUserActionResponse>> => {
  return adminUserService.custom("post", "/bulk/remove-role", data);
};

// Export default service object for backward compatibility
const AdminUserService = {
  getUsers,
  getUserDetail,
  createUser,
  updateUser,
  deleteUser,
  activateUser,
  deactivateUser,
  verifyUserEmail,
  assignRole,
  removeRole,
  getAvailableRoles,
  getUserStatistics,
  exportUsers,
  downloadFile,
  unlockUser,
  adminResetPassword,
  getUserSessions,
  revokeAllSessions,
  revokeSession,
  getUserAddresses,
  createUserAddress,
  updateUserAddress,
  deleteUserAddress,
  setDefaultAddress,
  getUserLoginHistory,
  bulkActivateUsers,
  bulkDeactivateUsers,
  bulkAssignRole,
  bulkRemoveRole,
};

export default AdminUserService;
